/**
 * Setup-panel body: progress steps + (once a template is picked) the Name
 * and Calendar fields. Mounted as `panelBody` inside BuilderShell.
 * Spec: assets/handoffs/design_handoff_new_calendar_page/README.md §4-§5
 *
 * Only states 01-04 are wired here (Phase 1) — quota/failure/blocked
 * variants (states 06-08) are deferred, see the project plan.
 *
 * @param {string=} templateTitle - selected template's display name, or falsy if none picked
 * @param {function} onChangeTemplate - "Change" link handler, returns to the template grid
 * @param {string} name
 * @param {string} integrationUuid
 * @param {{nameError?:string, integrationError?:string}} errors
 * @param {boolean} submitted - whether Create has been pressed at least once (state 03's "One thing left below." only shows after a press)
 * @param {Array<{uuid:string,label:string,healthy:boolean}>} integrationOptions
 * @param {function} onChangeName
 * @param {function} onChangeIntegration
 * @param {boolean=false} disabled
 */
import React from "react";
import ProgressSteps from "../../theme/untitled/ProgressSteps/ProgressSteps";
import SetupFields from "./SetupFields";
import styles from "./SetupPanel.module.scss";

const SetupPanel = ({
  templateTitle,
  onChangeTemplate,
  name,
  integrationUuid,
  errors = {},
  submitted = false,
  integrationOptions,
  onChangeName,
  onChangeIntegration,
  disabled = false,
}) => {
  const step1Done = !!templateTitle;
  const selectedIntegration = integrationOptions.find((o) => o.uuid === integrationUuid);
  const step2Done = step1Done && !!name && !!selectedIntegration;
  const step3Current = step2Done;

  const steps = [
    {
      key: "template",
      status: step1Done ? "done" : "current",
      title: step1Done ? templateTitle : "Choose a template",
      subtitle: step1Done ? (
        <>
          Template chosen <span aria-hidden="true">·</span>{" "}
          <button type="button" className={styles.changeLink} onClick={onChangeTemplate}>
            Change
          </button>
        </>
      ) : (
        "Pick one from the grid to carry on."
      ),
    },
    {
      key: "name-and-connect",
      status: !step1Done ? "upcoming" : step2Done ? "done" : "current",
      title: step2Done ? name : "Name it and connect a calendar",
      subtitle: step2Done
        ? selectedIntegration.label
        : submitted && errors.integrationError
        ? "One thing left below."
        : null,
    },
    {
      key: "create",
      status: !step3Current ? "upcoming" : "current",
      title: "Create",
      subtitle: step3Current ? "Nothing is public until you publish it." : null,
    },
  ];

  return (
    <>
      <ProgressSteps steps={steps} />
      {step1Done && (
        <SetupFields
          name={name}
          nameError={submitted ? errors.nameError : null}
          integrationUuid={integrationUuid}
          integrationError={submitted ? errors.integrationError : null}
          integrationOptions={integrationOptions}
          onChangeName={onChangeName}
          onChangeIntegration={onChangeIntegration}
          disabled={disabled}
        />
      )}
    </>
  );
};

export default SetupPanel;
